import styled from 'styled-components';
import { Button } from '@/components/common';
import { ErrorPageProps } from '@/Models';
import { FaExclamationTriangle } from 'react-icons/fa';

/**
 * ErrorPage Component
 */
export const ErrorPage: React.FC<ErrorPageProps> = ({
  icon: IconComponent = FaExclamationTriangle,
  iconColor,
  title = 'Oops! Something went wrong',
  message = 'We encountered an unexpected error. Please try again later.',
  buttonText = 'Exit',
  onButtonClick,
}) => {
  return (
    <ErrorContainer>
      <ErrorContent>
        <ErrorIconWrapper $color={iconColor}>
          <IconComponent />
        </ErrorIconWrapper>
        <ErrorTitle>{title}</ErrorTitle>
        <ErrorMessage>{message}</ErrorMessage>
        <Button
          $size="lg"
          onClick={onButtonClick}
          aria-label={buttonText}
        >
          {buttonText}
        </Button>
      </ErrorContent>
    </ErrorContainer>
  );
};

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.bgSecondary};
`;

const ErrorContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  max-width: 480px;
  width: 100%;
  gap: ${({ theme }) => theme.spacing.xl};
`;

const ErrorIconWrapper = styled.div<{ $color?: string }>`
  font-size: 120px;
  line-height: 1;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ $color, theme }) => $color || theme.colors.error};
  display: flex;
  align-items: center;
  justify-content: center;
  
  /* Subtle animation for visual interest */
  animation: gentleBounce 2s ease-in-out infinite;
  
  @keyframes gentleBounce {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-10px);
    }
  }
  
  svg {
    width: 120px;
    height: 120px;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 80px;
    
    svg {
      width: 80px;
      height: 80px;
    }
  }
`;

const ErrorTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }
`;

const ErrorMessage = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  color: ${({ theme }) => theme.colors.text.muted};
  margin: 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  max-width: 400px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;
